import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { LineChart, TrendingUp } from "lucide-react";

const BG_URL = "https://images.unsplash.com/photo-1714548529197-537c1f0b6aa7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjB0ZWNobm9sb2d5JTIwZ3JpZHxlbnwwfHx8fDE3ODQzNDI1MTV8MA&ixlib=rb-4.1.0&q=85";

export default function Login({ mode = "login" }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const nav = useNavigate();
  const isRegister = mode === "register";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (isRegister) await register(email, password, name);
      else await login(email, password);
      toast.success(isRegister ? "Account created" : "Welcome back");
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Authentication failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex" data-testid="auth-page">
      <div
        className="hidden lg:block lg:w-1/2 relative"
        style={{ backgroundImage: `linear-gradient(135deg, rgba(0,0,0,0.85), rgba(0,0,0,0.7)), url(${BG_URL})`, backgroundSize: "cover", backgroundPosition: "center" }}
      >
        <div className="absolute inset-0 p-12 flex flex-col justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-gradient-to-br from-blue-500 to-cyan-400 grid place-items-center">
              <LineChart className="w-5 h-5 text-black" />
            </div>
            <div>
              <div className="font-heading font-bold tracking-tight text-lg leading-none">ALGO<span className="text-blue-400">DESK</span></div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">Options Terminal</div>
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Automation for Serious Traders</div>
            <h1 className="font-heading text-4xl font-bold text-white leading-tight mb-3">Time-based options on NIFTY & SENSEX. Fully automated.</h1>
            <p className="text-slate-400 text-sm max-w-md">Deploy delta-neutral strategies with SL-to-BE protection. Test on paper, go live when ready. Zerodha & Upstox supported.</p>
            <div className="mt-6 grid grid-cols-3 gap-3 max-w-md">
              {[
                { k: "In-house Δ", v: "Black-Scholes" },
                { k: "ODTE", v: "Current Expiry" },
                { k: "Modes", v: "Paper + Live" },
              ].map((s) => (
                <div key={s.k} className="border border-white/10 rounded-sm p-3">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">{s.k}</div>
                  <div className="font-mono text-sm text-slate-200 mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid={isRegister ? "register-form" : "login-form"}>
          <div>
            <div className="flex items-center gap-2 mb-2 lg:hidden">
              <div className="w-8 h-8 rounded-sm bg-gradient-to-br from-blue-500 to-cyan-400 grid place-items-center">
                <TrendingUp className="w-4 h-4 text-black" />
              </div>
              <div className="font-heading font-bold tracking-tight">ALGO<span className="text-blue-400">DESK</span></div>
            </div>
            <h2 className="font-heading text-2xl font-bold">{isRegister ? "Create account" : "Sign in"}</h2>
            <p className="text-sm text-slate-400 mt-1">{isRegister ? "Start automating in under 2 minutes." : "Access your algo trading terminal."}</p>
          </div>
          {isRegister && (
            <div className="space-y-1.5">
              <Label htmlFor="name" className="text-xs uppercase tracking-widest text-slate-400">Name</Label>
              <Input id="name" data-testid="register-name-input" value={name} onChange={(e) => setName(e.target.value)} required
                     className="bg-[#141414] border-white/10 h-10" placeholder="Your name" />
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-xs uppercase tracking-widest text-slate-400">Email</Label>
            <Input id="email" type="email" data-testid={isRegister ? "register-email-input" : "login-email-input"}
                   value={email} onChange={(e) => setEmail(e.target.value)} required
                   className="bg-[#141414] border-white/10 h-10" placeholder="you@trader.com" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password" className="text-xs uppercase tracking-widest text-slate-400">Password</Label>
            <Input id="password" type="password" data-testid={isRegister ? "register-password-input" : "login-password-input"}
                   value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6}
                   className="bg-[#141414] border-white/10 h-10" placeholder="••••••••" />
          </div>
          <Button type="submit" disabled={busy}
                  data-testid={isRegister ? "register-submit-btn" : "login-submit-btn"}
                  className="w-full h-10 bg-blue-500 hover:bg-blue-400 text-black font-semibold">
            {busy ? "…" : isRegister ? "Create account" : "Sign in"}
          </Button>
          <div className="text-xs text-slate-400 text-center">
            {isRegister ? (
              <>Already have an account? <Link to="/login" className="text-blue-400 hover:underline" data-testid="link-to-login">Sign in</Link></>
            ) : (
              <>New here? <Link to="/register" className="text-blue-400 hover:underline" data-testid="link-to-register">Create an account</Link></>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

